import React, { useState, useEffect } from 'react';
import { Box, Alert, AlertTitle, Button, Typography, LinearProgress } from '@mui/material';
import { Warning as WarningIcon, Upgrade as UpgradeIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from '../../utils/axios';
import { isSubscriptionEnforcementEnabled } from '../../config/features';

const TrialBanner = () => {
    const enforcementEnabled = isSubscriptionEnforcementEnabled();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [daysRemaining, setDaysRemaining] = useState(null);

    useEffect(() => {
        if (!enforcementEnabled) {
            setLoading(false);
            return undefined;
        }

        fetchSubscriptionStatus();
        const interval = setInterval(() => {
            if (subscription?.trialEndsAt) {
                updateDaysRemaining(subscription.trialEndsAt);
            }
        }, 1000 * 60 * 60);

        return () => clearInterval(interval);
    }, [enforcementEnabled, subscription?.trialEndsAt]);

    const fetchSubscriptionStatus = async () => {
        try {
            const response = await axios.get('/subscription/status');
            const sub = response.data.data.subscription;
            setSubscription(sub);
            if (sub.trialEndsAt) {
                updateDaysRemaining(sub.trialEndsAt);
            }
        } catch (error) {
            console.error('Failed to fetch subscription status');
        } finally {
            setLoading(false);
        }
    };

    const updateDaysRemaining = (trialEndsAt) => {
        const now = new Date();
        const endDate = new Date(trialEndsAt);
        const diffTime = endDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysRemaining(diffDays > 0 ? diffDays : 0);
    };

    if (!enforcementEnabled || loading || !subscription) {
        return null;
    }

    if (subscription.status !== 'TRIAL' || !subscription.trialEndsAt) {
        return null;
    }

    const isExpiringSoon = daysRemaining <= 3 && daysRemaining > 0;
    const isExpired = daysRemaining <= 0;
    const rowSx = {
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 1.5,
    };
    const ctaSx = { width: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 2 }, flexShrink: 0 };

    if (isExpired) {
        return (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <AlertTitle>Free Trial Expired</AlertTitle>
                <Box sx={rowSx}>
                    <Typography>
                        Your free trial has ended. Please choose a subscription plan to continue using the ERP.
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<UpgradeIcon />}
                        component={RouterLink}
                        to="/subscription/pricing"
                        sx={ctaSx}
                    >
                        Choose Plan
                    </Button>
                </Box>
            </Alert>
        );
    }

    const totalTrialDays = 10;
    const daysUsed = totalTrialDays - daysRemaining;
    const progressPercentage = (daysUsed / totalTrialDays) * 100;

    return (
        <Alert severity={isExpiringSoon ? 'warning' : 'info'} icon={<WarningIcon />} sx={{ mb: 2 }}>
            <AlertTitle>
                {isExpiringSoon ? 'Trial Ending Soon!' : 'Free Trial Active'}
            </AlertTitle>
            <Box>
                <Box sx={{ ...rowSx, mb: 1 }}>
                    <Typography variant="body2">
                        {daysRemaining === 1
                            ? '1 day remaining in your free trial'
                            : `${daysRemaining} days remaining in your free trial`}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<UpgradeIcon />}
                        component={RouterLink}
                        to="/subscription/pricing"
                        sx={ctaSx}
                    >
                        Choose Plan
                    </Button>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progressPercentage}
                    sx={{ height: 6, borderRadius: 3 }}
                />
            </Box>
        </Alert>
    );
};

export default TrialBanner;
